import { useMemo } from 'react'
import { useHits } from 'react-instantsearch'

import { uniqBy } from 'lodash'

import HitBrandCard from 'components/molecules/hit-brand-card'
import { ProductItem } from 'components/molecules/hit-product-card/type'
import { useGetFavouriteBrands } from 'features/retailer/favourites/hooks/use-get-all-favourite-brands'

import { Brand, useBrands } from 'hooks/common/use-brands'
import useGetBrandListMovByCurrency from 'hooks/currency/use-get-brand-list-mov'
import { useIsLoggedIn } from 'hooks/use-is-logged-in'

import { roundUpToNearest } from 'utils/common'
import { getMapFromArrayByKey } from 'utils/data-structure'
import { formatPrice } from 'utils/functions'
import { fireTrackingEvent } from 'utils/tracking'

import css from './hit-product-list.module.scss'

type HitBrandListProps = {
  currency: string
  openSignUp?: () => void
}

const HitBrandList = ({ currency, openSignUp }: HitBrandListProps) => {
  const { hits } = useHits<ProductItem>()
  const uniqHits = uniqBy(hits, (hit) => hit.attributes['brand-key'] as string)

  const { data: brands, isLoading } = useBrands()
  const brandsByName = useMemo(() => getMapFromArrayByKey<Brand>(brands ?? [], 'storeKey'), [brands])

  const brandIds: string[] = []

  uniqHits.forEach((hit) => {
    const brandKey = hit.attributes['brand-key'] as string
    const brandInfo = brands ? brandsByName[brandKey] : undefined

    if (brandInfo?.id) {
      brandIds.push(brandInfo.id)
    }
  })

  const { data: BrandListMov } = useGetBrandListMovByCurrency(brandIds, currency)

  const { isLoggedIn } = useIsLoggedIn()
  const { data: favouriteBrands } = useGetFavouriteBrands(isLoggedIn)

  return (
    <section className={css.container}>
      {uniqHits.map((hit) => {
        const brandKey = hit.attributes['brand-key'] as string
        const brandInfo = brands ? brandsByName[brandKey] : undefined
        const brandMov = BrandListMov && brandInfo?.id ? BrandListMov[brandInfo.id] : undefined

        let brandMovDisplay = currency + ' '
        if (brandMov) {
          brandMovDisplay = formatPrice(roundUpToNearest(brandMov, 1), currency)
        }

        return (
          <div
            className={css.brandCardWrapper}
            key={brandKey}
            onClick={() => fireTrackingEvent('track', { event: 'product_card_brand_click', productCardName: brandKey })}
          >
            <HitBrandCard
              hit={hit}
              key={hit.productID}
              brand={brandInfo}
              isLoading={isLoading}
              isLoggedIn={isLoggedIn}
              brandMovDisplay={brandMovDisplay}
              isFavourite={favouriteBrands?.some((brand) => brand.key === brandKey)}
              openSignUp={openSignUp}
            />
          </div>
        )
      })}
    </section>
  )
}

export default HitBrandList
